import React, { useEffect, useMemo, useState, useCallback } from "react";
import { 
  RefreshCw, 
  Trash2, 
  Phone, 
  Search, 
  X, 
  Inbox, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  TrendingUp, 
  MessageCircle,
  ExternalLink 
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import AdminNav from "../components/admin/AdminNav";

const STATUS_LABELS = {
  new: { label: "Новая", cls: "bg-[#4A1419] text-[#F5EFE6] border-[#4A1419]" },
  in_progress: { label: "В работе", cls: "bg-[#C5A059] text-[#1A0E07] border-[#C5A059]" },
  done: { label: "Выполнена", cls: "bg-[#EFE6D8] text-[#4A1419] border-[#4A1419]" },
  cancelled: { label: "Отменена", cls: "bg-gray-200 text-gray-500 border-gray-300 line-through" },
};
const STATUS_OPTIONS = Object.keys(STATUS_LABELS);

const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });
  } catch { return s; }
};

const StatCard = ({ icon: Icon, label, value, tone = "light" }) => (
  <div className={`border-2 border-[#4A1419] p-5 md:p-6 relative ${tone === "dark" ? "bg-[#4A1419] text-[#F5EFE6]" : "bg-[#FDFBF7] text-[#1A0E07]"}`}>
    <div className={`flex items-center justify-between ${tone === "dark" ? "text-[#F5EFE6]/80" : "text-[#6E5549]"}`}>
      <span className="text-[10px] uppercase tracking-[0.2em] font-bold">{label}</span>
      <Icon size={18} strokeWidth={1.75} />
    </div>
    <div className={`mt-3 font-heading text-4xl md:text-5xl heading-tight ${tone === "dark" ? "text-[#D4AF37]" : "text-[#4A1419]"}`}>
      {value}
    </div>
  </div>
);

export default function AdminDashboard() {
  const { admin } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads"),
      ]);
      setStats(s.data);
      setLeads(l.data);
    } catch (err) {
      toast.error(formatApiError(err, "Не удалось загрузить заявки"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const updateStatus = async (leadId, nextStatus) => {
    try {
      await api.patch(`/admin/leads/${leadId}/status`, { status: nextStatus });
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, status: nextStatus } : l))
      );
      toast.success("Статус заявки обновлен");
    } catch (err) {
      toast.error(formatApiError(err, "Не удалось изменить статус"));
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Удалить эту заявку?")) return;
    try {
      await api.delete(`/admin/leads/${leadId}`);
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
      toast.success("Заявка удалена");
    } catch (err) {
      toast.error(formatApiError(err, "Не удалось удалить заявку"));
    }
  };

  const filteredLeads = useMemo(() => {
    let res = leads;
    if (statusFilter !== "all") {
      res = res.filter((l) => l.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      res = res.filter(
        (l) =>
          l.name?.toLowerCase().includes(q) ||
          l.phone?.toLowerCase().includes(q) ||
          l.comment?.toLowerCase().includes(q) ||
          l.service?.toLowerCase().includes(q)
      );
    }
    return res;
  }, [leads, statusFilter, search]);

  return (
    <div className="min-h-screen bg-[#F5EFE6] text-[#1A0E07]">
      <AdminNav />

      <main className="max-w-[1500px] mx-auto px-4 md:px-8 py-8 space-y-8">
        {/* Header Title & Refresh */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b-2 border-[#4A1419]/20">
          <div>
            <h1 className="font-heading text-3xl md:text-5xl text-[#4A1419] font-bold">
              Входящие заявки с сайта
            </h1>
            <p className="text-sm text-[#6E5549] mt-1">
              Обращения посетителей через онлайн-форму сайта. Для ведения заказа в цеху используйте ссылку «В CRM».
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadAll}
              disabled={loading}
              className="p-2.5 bg-[#F5EFE6] border-2 border-[#4A1419]/30 hover:border-[#4A1419] text-[#4A1419] transition-colors"
              title="Обновить список"
            >
              <RefreshCw size={17} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
          <StatCard
            icon={Inbox}
            label="Новых"
            value={stats?.by_status?.new ?? 0}
            tone="dark"
          />
          <StatCard
            icon={Clock}
            label="В работе"
            value={stats?.by_status?.in_progress ?? 0}
          />
          <StatCard
            icon={CheckCircle2}
            label="Выполнено"
            value={stats?.by_status?.done ?? 0}
          />
          <StatCard
            icon={TrendingUp}
            label="Сегодня"
            value={stats?.today_count ?? 0}
          />
          <StatCard
            icon={TrendingUp}
            label="За 7 дней"
            value={stats?.last_7d_count ?? 0}
            tone="dark"
          />
        </div>

        {/* Search Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
          <div className="font-heading text-2xl md:text-3xl text-[#4A1419] font-bold">
            Список заявок <span className="text-lg font-body text-[#6E5549]">· {filteredLeads.length}</span>
          </div>

          <div className="relative w-full md:w-80">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6E5549]" />
            <input
              type="text"
              placeholder="Поиск по имени, телефону…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-white border-2 border-[#4A1419]/30 focus:border-[#4A1419] outline-none pl-10 pr-10 py-2 text-sm"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E5549] hover:text-[#4A1419]"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        {/* Status Filters */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setStatusFilter("all")}
            className={`px-3.5 py-1.5 text-xs uppercase tracking-wider font-bold border-2 transition-colors ${
              statusFilter === "all"
                ? "bg-[#4A1419] text-[#F5EFE6] border-[#4A1419]"
                : "border-[#4A1419]/30 text-[#4A1419] hover:border-[#4A1419] bg-white"
            }`}
          >
            Все · {leads.length}
          </button>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3.5 py-1.5 text-xs uppercase tracking-wider font-bold border-2 transition-colors ${
                statusFilter === s
                  ? "bg-[#4A1419] text-[#F5EFE6] border-[#4A1419]"
                  : "border-[#4A1419]/30 text-[#4A1419] hover:border-[#4A1419] bg-white"
              }`}
            >
              {STATUS_LABELS[s].label} · {leads.filter((l) => l.status === s).length}
            </button>
          ))}
        </div>

        {/* Leads Table */}
        <div className="border-2 border-[#4A1419] bg-white overflow-x-auto shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#4A1419] text-[#F5EFE6]">
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-[11px]">Дата</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-[11px]">Имя</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-[11px]">Телефон & Связь</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-[11px]">Услуга</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-[11px]">Комментарий</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-[11px]">Статус</th>
                <th className="px-4 py-3 font-bold uppercase tracking-wider text-[11px] text-right">Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-[#6E5549]">
                    {loading ? "Загружаем…" : "Заявок пока нет"}
                  </td>
                </tr>
              )}
              {filteredLeads.map((l) => {
                const cleanPhone = (l.phone || "").replace(/\D/g, "");
                const waUrl = cleanPhone ? `https://wa.me/${cleanPhone.startsWith('8') ? '7' + cleanPhone.slice(1) : cleanPhone}` : null;
                
                return (
                  <tr key={l.id} className="border-t border-[#4A1419]/15 hover:bg-[#FDFBF7] transition-colors align-top">
                    <td className="px-4 py-3 text-[#6E5549] whitespace-nowrap text-xs">
                      {fmtDate(l.created_at)}
                    </td>
                    <td className="px-4 py-3 font-bold text-[#1A0E07]">
                      {l.name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <a
                          href={`tel:${l.phone}`}
                          className="text-[#4A1419] font-semibold hover:underline inline-flex items-center gap-1 text-xs"
                        >
                          <Phone size={12} /> {l.phone}
                        </a>
                        {waUrl && (
                          <a
                            href={waUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 bg-emerald-100 text-emerald-800 rounded hover:bg-emerald-200"
                            title="Написать в WhatsApp"
                          >
                            <MessageCircle size={13} />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-[#1A0E07]">
                      {l.service || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6E5549] max-w-xs">
                      {l.comment || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        className={`px-2.5 py-1 border-2 text-[10px] uppercase tracking-wider font-bold cursor-pointer ${STATUS_LABELS[l.status]?.cls || ""}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s} className="bg-white text-black normal-case">
                            {STATUS_LABELS[s].label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <a
                          href="/crm/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold bg-[#141210] text-[#D4AF37] border border-[#D4AF37] hover:bg-[#201D1A] transition-all shadow-xs"
                          title="Открыть заказ в CRM цеха"
                        >
                          <span>В CRM</span>
                          <ExternalLink size={11} strokeWidth={2.2} />
                        </a>
                        <button
                          onClick={() => deleteLead(l.id)}
                          className="p-1.5 text-[#6E5549] hover:text-red-700 hover:bg-red-50 transition-colors"
                          title="Удалить заявку"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
